import { motion } from "framer-motion";
import { css, styled } from "styled-components";

import { AirdropNFTStatus } from "@/interfaces/nft";
import { AirdropStatus } from "@/interfaces/store";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const BuyHapi = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  color: ${({ theme }) => theme.colors.blue};
  font-family: Inter;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 19.6px;
  text-align: center;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;

  & > svg {
    fill: ${({ theme }) => theme.colors.blue};
  }
`;

const Airdrop = styled.div<{ $status: AirdropStatus }>`
  display: flex;
  flex-direction: column;
  padding: 1rem;
  gap: 0.75rem;
  margin-bottom: 1rem;
  border-radius: 1.25rem;
  background: ${({ theme }) => theme.colors.airdrop.bg};

  ${({ theme, $status }) => {
    switch ($status) {
      case "soon":
      case "preActive":
        return css`
          border: 1px solid ${theme.colors.pink};
          box-shadow: 4px 4px 0px 0px ${theme.colors.pink};
        `;
      case "active":
        return css`
          border: 1px solid ${theme.colors.airdrop.yellow};
          box-shadow: 4px 4px 0px 0px ${theme.colors.airdrop.yellow};
        `;
      case "stopped":
        return css`
          border: 1px solid ${theme.colors.airdrop.red};
          box-shadow: 4px 4px 0px 0px ${theme.colors.airdrop.red};
        `;

      default:
        return css`
          border: 1px solid ${theme.colors.airdrop.default};
          box-shadow: 4px 4px 0px 0px ${theme.colors.airdrop.default};
        `;
    }
  }}
`;

const Row = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const Image = styled.div`
  overflow: hidden;

  display: flex;
  width: 5.5rem;
  height: 5.5rem;
  padding: 0.5625rem;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.surfaceQuaternary};

  & > img {
    border-radius: 50%;
    width: 100%;
    height: 100%;
  }
`;

const BaseInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  flex: 1;
  & > p {
    font-style: normal;
    &:first-of-type {
      color: ${({ theme }) => theme.colors.black};
      font-size: 1rem;
      font-weight: 700;
      line-height: 120%;
      letter-spacing: -0.02rem;
    }
    &:last-of-type {
      color: ${({ theme }) => theme.colors.secondaryText};
      font-size: 0.75rem;
      font-weight: 600;
      line-height: 140%;
    }
  }
`;

const Socials = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.375rem;
  & > a {
    color: ${({ theme }) => theme.colors.black};
    & > svg {
      cursor: pointer;
      width: 1.5rem;
      height: 1.5rem;
    }
  }
`;

const ClaimedContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0.5rem;

  border-radius: 0.5rem;
  background-color: ${({ theme }) => theme.colors.surfaceQuaternary};

  & > div {
    display: flex;
    justify-content: space-between;
    & > p {
      color: ${({ theme }) => theme.colors.black};
      font-size: 0.75rem;
      font-style: normal;
      font-weight: 600;
      line-height: 140%;
    }
  }
`;

const Progress = styled.progress`
  width: 100%;
  -webkit-appearance: none;
  appearance: none;
  border: none;
  border-radius: 1.5rem;
  background: ${({ theme }) => theme.colors.airdrop.progressBarBg};
  &::-moz-progress-bar {
    background: ${({ theme }) => theme.colors.airdrop.progressBarValue};
    border-radius: 1.5rem;
    border: 1px solid ${({ theme }) => theme.colors.black};
    transition: width 1s ease;
  }
  &::-webkit-progress-value {
    background: ${({ theme }) => theme.colors.airdrop.progressBarValue};
    border-radius: 1.5rem;
    border: 1px solid ${({ theme }) => theme.colors.black};
    transition: width 1s ease;
  }
  &::-webkit-progress-bar {
    background: transparent;
    border-radius: 1.5rem;
  }

  &:not([value]) {
    &::-moz-progress-bar {
      display: none;
    }
    &::-webkit-progress-value {
      display: none;
    }
  }
`;

const Conditionals = styled.div`
  display: flex;
  flex-direction: row;

  & > div {
    min-height: 4.5rem;
    display: flex;
    flex-direction: column;
    padding: 0.75rem;
    align-items: stretch;
    gap: 0.5rem;
    flex: 1 0 0;
    border-radius: 1rem;
    border: 1px solid ${({ theme }) => theme.colors.black};
    box-shadow: 2px 2px 0px 0px ${({ theme }) => theme.colors.black};
    background: rgba(249, 243, 242, 0.7);
    & > div {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      flex: 1 0 0;
      & > p {
        color: ${({ theme }) => theme.colors.black};
        font-size: 1rem;
        font-style: bold;
        font-weight: 800;
        line-height: 120%;
        letter-spacing: -0.015rem;
      }
    }
    & > p {
      color: ${({ theme }) => theme.colors.secondaryText};
      font-size: 0.75rem;
      font-style: normal;
      font-weight: 600;
      line-height: 120%;
      letter-spacing: -0.015rem;
    }
  }
`;

const NFT = {
  List: styled(motion.div)<{ $isClosed: boolean }>`
    display: flex;
    flex-direction: column;
    overflow: hidden;
    opacity: ${({ $isClosed }) => ($isClosed ? 0.5 : 1)};

    & > div {
      &:last-child {
        padding: 0;
      }
    }
  `,

  Component: styled.div`
    flex-shrink: 0;
    padding-bottom: 0.75rem;

    display: flex;
    justify-content: stretch;
    align-items: center;
    gap: 0.75rem;
    & > img {
      flex-shrink: 0;
      width: 3.625rem;
      height: 3.625rem;

      border-radius: 0.5rem;
      border: 1px solid ${({ theme }) => theme.colors.black};
    }

    & > div:first-of-type {
      justify-items: start;
      flex-grow: 1;
      display: flex;
      flex-direction: column;
      gap: 0.375rem;
    }
    & > button {
      margin-right: 4px;
    }
  `,

  Text: styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;

    color: ${({ theme }) => theme.colors.black};
    font-size: 1rem;
    font-weight: 700;
    line-height: 120%;
    letter-spacing: -0.02rem;
  `,

  Circle: styled.div<{ $status: AirdropNFTStatus }>`
    flex-shrink: 0;
    width: 1.25rem;
    height: 1.25rem;

    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.625rem;

    border-radius: 50%;
    background-color: ${({ theme, $status }) => ($status === "fulfilled" ? theme.colors.green : theme.colors.red)};
    color: ${({ theme }) => theme.colors.white};
    & > svg {
      width: 1rem;
      height: 1rem;
    }
  `,

  Holder: styled.div`
    display: flex;

    align-items: center;
    gap: 0.375rem;

    border-radius: 0.5rem;
    & > svg {
      width: 1.25rem;
      height: auto;
    }
    & > div {
      display: flex;
      gap: 0.25rem;

      /* color: ${({ theme }) => theme.colors.black}; */
      font-size: 0.75rem;
      font-style: normal;
      font-weight: 600;
      line-height: 140%;
    }
  `,

  ClaimedByUser: styled.div`
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.375rem 0.5rem;

    border-radius: 0.5rem;
    background-color: ${({ theme }) => theme.colors.airdrop.lightGreen};

    color: ${({ theme }) => theme.colors.green};
    font-size: 0.75rem;
    font-style: normal;
    font-weight: 600;
    line-height: 120%;
    letter-spacing: -0.015rem;

    & > svg {
      flex-shrink: 0;
      width: 1rem;
      height: 1rem;
    }
  `,
};

const ShowMore = styled.button`
  align-self: center;
  border: none;
  color: ${({ theme }) => theme.colors.blue};
  width: fit-content;
  background-color: transparent;
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 600;
  line-height: 140%;
`;

const Badge = styled.div<{ $status: AirdropStatus }>`
  width: fit-content;
  display: flex;
  padding: 0.25rem 0.5rem;
  gap: 0.25rem;

  border-radius: 0.5rem;

  & > p {
    color: ${({ theme }) => theme.colors.black};
    font-size: 0.875rem;
    font-weight: 600;
    line-height: 140%;
  }

  ${({ $status, theme }) => {
    switch ($status) {
      case "soon":
      case "preActive":
        return css`
          background-color: ${theme.colors.pink};
          position: relative;
          & > p {
            z-index: 10;
          }
          & > svg {
            position: absolute;
            opacity: 0.4;
            &:first-of-type {
              width: 1rem;
              height: 1rem;
              left: 7%;
              top: 10%;
            }
            &:last-of-type {
              width: 0.75rem;
              height: 0.75rem;
              right: 2%;
              bottom: -10%;
            }
          }
        `;
      case "closed":
        return css`
          background-color: ${theme.colors.surfaceQuaternary};
        `;

      default:
        return;
    }
  }}
`;

export default {
  Container,
  Airdrop,
  Row,
  Image,
  BaseInfo,
  Socials,
  ClaimedContainer,
  NFT,
  Progress,
  Conditionals,
  ShowMore,
  Badge,
  BuyHapi,
};
